import React, { useEffect, useRef, useState } from 'react';

/**
 * A component that renders the quilt directly to canvas
 * This ensures that preview and download appear identical
 */
const CanvasQuiltRenderer = ({
  selectedLayout,
  showBinding,
  showBorder,
  bindingColor,
  borderColor,
  embossedItems,
  textItems,
  width = 800,
  height = 800,
  canvasRef,
  onRenderComplete,
  firestoreDimensions
}) => {
  const internalCanvasRef = useRef(null);
  const [isRendering, setIsRendering] = useState(true);
  const [renderError, setRenderError] = useState(null);
  const [canvasDimensions, setCanvasDimensions] = useState({ width, height });
  const [displayInfo, setDisplayInfo] = useState(null);
  // Add state to track analyzed images with a more robust structure
  const [analyzedDimensions, setAnalyzedDimensions] = useState(null);
  // Add a ref to track if we need to trigger a re-render based on analyzed dimensions
  const shouldUpdateFromAnalysis = useRef(false);

  // Use the provided ref or our internal one
  const effectiveCanvasRef = canvasRef || internalCanvasRef;

  // Enhanced utility function to get dimensions from storage with better prioritization
  const getTileDimensionsFromStorage = () => {
    try {
      // First check if we have analyzed dimensions (highest priority)
      const analyzedDimStr = localStorage.getItem('analyzedTileDimensions');
      if (analyzedDimStr) {
        const dimensions = JSON.parse(analyzedDimStr);
        if (dimensions && dimensions.tileWidthPixels && dimensions.tileHeightPixels) {
          console.log('Retrieved analyzed dimensions from localStorage:', dimensions);
          return {
            ...dimensions,
            source: 'analyzed'
          };
        }
      }
      
      // Then try quiltTileDimensions (second priority)
      const storedDimensions = localStorage.getItem('quiltTileDimensions');
      if (storedDimensions) {
        const dimensions = JSON.parse(storedDimensions);
        if (dimensions && dimensions.tileWidthPixels && dimensions.tileHeightPixels) {
          console.log('Retrieved tile dimensions from localStorage:', dimensions);
          return {
            ...dimensions,
            source: 'localStorage'
          };
        }
      }
      
      // Try sessionStorage as fallback (third priority)
      const sessionDimensions = sessionStorage.getItem('quiltDimensions');
      if (sessionDimensions) {
        const dimensions = JSON.parse(sessionDimensions);
        if (dimensions && dimensions.tileWidthPixels && dimensions.tileHeightPixels) {
          console.log('Retrieved tile dimensions from sessionStorage:', dimensions);
          return {
            ...dimensions,
            source: 'sessionStorage'
          };
        }
      }
    } catch (error) {
      console.error('Error getting dimensions from storage:', error);
    }
    return null;
  };
  
  // Enhanced function to consolidate image analysis data with better precision
  const consolidateImageAnalysis = (width, height, count = 1) => {
    // Don't analyze embossed items or very small/large images
    if (width < 50 || height < 50 || width > 1000 || height > 1000) return;
    
    setAnalyzedDimensions(prev => {
      if (!prev) {
        // First image analyzed
        return {
          tileWidthPixels: width,
          tileHeightPixels: height,
          count: count,
          timestamp: new Date().toISOString(),
          // Track occurrences of specific dimensions
          occurrences: { [`${width}x${height}`]: count }
        };
      }
      
      // Update occurrences counter for this dimension
      const dimensionKey = `${width}x${height}`;
      const occurrences = { ...prev.occurrences };
      occurrences[dimensionKey] = (occurrences[dimensionKey] || 0) + count;
      
      // Find most common dimension
      let mostCommonDimension = dimensionKey;
      let highestCount = occurrences[dimensionKey];
      
      Object.entries(occurrences).forEach(([dim, count]) => {
        if (count > highestCount) {
          mostCommonDimension = dim;
          highestCount = count;
        }
      });
      
      // Get the most common dimensions
      const [commonWidth, commonHeight] = mostCommonDimension.split('x').map(Number);
      
      // If we have a clear winner with at least 5 occurrences, use that
      if (highestCount >= 5 && mostCommonDimension !== `${prev.tileWidthPixels}x${prev.tileHeightPixels}`) {
        console.log(`Switching to most common dimensions: ${mostCommonDimension} (${highestCount} occurrences)`);
        shouldUpdateFromAnalysis.current = true;
        return {
          tileWidthPixels: commonWidth,
          tileHeightPixels: commonHeight,
          count: prev.count + count,
          timestamp: new Date().toISOString(),
          occurrences
        };
      }
      
      // Otherwise, just update the count
      return {
        ...prev,
        count: prev.count + count,
        timestamp: new Date().toISOString(),
        occurrences
      };
    });
  };
  
  // Effect to save analyzed dimensions when they change
  useEffect(() => {
    if (analyzedDimensions && analyzedDimensions.count > 0) {
      // Find the most common dimension
      let mostCommonDimension = '250x300'; // Default to what we're seeing in logs
      let highestCount = 0;
      
      if (analyzedDimensions.occurrences) {
        Object.entries(analyzedDimensions.occurrences).forEach(([dim, count]) => {
          if (count > highestCount) {
            mostCommonDimension = dim;
            highestCount = count;
          }
        });
      }
      
      // Use the most common dimensions if we have at least 5 of them
      if (highestCount >= 3) {
        const [commonWidth, commonHeight] = mostCommonDimension.split('x').map(Number);
        
        try {
          // Get grid dimensions from current display info
          const gridInfo = {
            rows: displayInfo?.gridRows || (firestoreDimensions?.rows || 4),
            columns: displayInfo?.gridCols || (firestoreDimensions?.columns || 4)
          };
          
          const dimensionsToStore = {
            tileWidthPixels: commonWidth,
            tileHeightPixels: commonHeight,
            ...gridInfo,
            count: highestCount,
            timestamp: new Date().toISOString(),
            source: 'image-analysis'
          };
          
          localStorage.setItem('analyzedTileDimensions', JSON.stringify(dimensionsToStore));
          console.log('Stored reliable image analysis:', dimensionsToStore);
          
          // If this is a significant update (250x300 instead of 300x300), trigger a refresh
          if (shouldUpdateFromAnalysis.current) {
            console.log('Detected significant dimension change - updating display');
            shouldUpdateFromAnalysis.current = false;
            
            // Force an update to use these new dimensions
            setDisplayInfo(prev => {
              if (!prev) return null;
              
              // Only update if significantly different
              if (Math.abs(commonWidth/commonHeight - prev.aspectRatio) > 0.05) {
                return {
                  ...prev,
                  tileWidth: commonWidth,
                  tileHeight: commonHeight,
                  aspectRatio: commonWidth / commonHeight,
                  dimensionSource: 'analyzed'
                };
              }
              return prev;
            });
          }
        } catch (e) {
          console.log('Could not save consolidated analysis', e);
        }
      }
    }
  }, [analyzedDimensions, displayInfo, firestoreDimensions]);

  // Calculate canvas dimensions based on grid and tile aspect ratio
  useEffect(() => {
    // Get grid size from Firestore if available
    let gridRows, gridCols;
    let tileWidthPixels, tileHeightPixels;
    let dimensionSource = 'default';

    // Priority 0: Use Firestore dimensions if available (most reliable)
    if (firestoreDimensions) {
        gridRows = firestoreDimensions.rows || 6;
        gridCols = firestoreDimensions.columns || 6;
        tileWidthPixels = firestoreDimensions.tileWidthPixels || 300;
        tileHeightPixels = firestoreDimensions.tileHeightPixels || 300;
        dimensionSource = 'firestore';
        console.log(`Using Firestore dimensions: ${gridCols}x${gridRows} grid, tiles: ${tileWidthPixels}x${tileHeightPixels}px`);
    }
    // Priority 1: Use analyzed dimensions if available and Firestore not available
    else {
        const storedAnalysis = localStorage.getItem('analyzedTileDimensions');
        if (storedAnalysis) {
            try {
                const analysis = JSON.parse(storedAnalysis);
                if (analysis && analysis.tileWidthPixels && analysis.tileHeightPixels && analysis.count >= 3) {
                    tileWidthPixels = analysis.tileWidthPixels;
                    tileHeightPixels = analysis.tileHeightPixels;
                    gridRows = analysis.rows || 6;
                    gridCols = analysis.columns || 6;
                    dimensionSource = 'analyzed';
                }
            } catch (e) {
                console.error('Error parsing stored analysis:', e);
            }
        }
        
        // Priority 2: Check localStorage/sessionStorage
        if (!tileWidthPixels || !tileHeightPixels) {
            const storedDimensions = getTileDimensionsFromStorage();
            if (storedDimensions) {
                if (storedDimensions.rows && storedDimensions.columns) {
                    gridRows = storedDimensions.rows;
                    gridCols = storedDimensions.columns;
                }
                if (storedDimensions.tileWidthPixels && storedDimensions.tileHeightPixels) {
                    tileWidthPixels = storedDimensions.tileWidthPixels;
                    tileHeightPixels = storedDimensions.tileHeightPixels;
                    dimensionSource = 'storage';
                }
            }
        }
        
        // Priority 3: Fall back to layout dimensions
        if (!gridRows || !gridCols || !tileWidthPixels || !tileHeightPixels) {
            if (selectedLayout) {
                if (selectedLayout.gridSize) {
                    const dimensions = selectedLayout.gridSize.split('x').map(Number);
                    gridCols = dimensions[0] || 6;
                    gridRows = dimensions[1] || 6;
                } else if (selectedLayout.original_grid_size) {
                    const dimensions = selectedLayout.original_grid_size.split('x').map(Number);
                    gridCols = dimensions[0] || 6;
                    gridRows = dimensions[1] || 6;
                } else {
                    gridCols = 6;
                    gridRows = 6;
                }
                
                // Try to extract actual tile dimensions from the first tile in tileData
                if (selectedLayout.tileData && Array.isArray(selectedLayout.tileData) && selectedLayout.tileData.length > 0) {
                    // Find first tile with actual dimensions
                    const tileWithDimensions = selectedLayout.tileData.find(tile => 
                        tile && tile.originalTile && (
                            (tile.originalTile.naturalWidth && tile.originalTile.naturalHeight) ||
                            (tile.originalTile.width && tile.originalTile.height)
                        )
                    );
                    
                    if (tileWithDimensions) {
                        const originalTile = tileWithDimensions.originalTile;
                        tileWidthPixels = originalTile.naturalWidth || originalTile.width || 300;
                        tileHeightPixels = originalTile.naturalHeight || originalTile.height || 300;
                        dimensionSource = 'layout-tile-data';
                        console.log(`Using dimensions from layout tile data: ${tileWidthPixels}x${tileHeightPixels}px`);
                    }
                }
                
                // If still not set, use defaults
                if (!tileWidthPixels || !tileHeightPixels) {
                    tileWidthPixels = 300;
                    tileHeightPixels = 300;
                    dimensionSource = 'default';
                }
            } else {
                // Default values if nothing else is available
                gridRows = 6;
                gridCols = 6;
                tileWidthPixels = 300;
                tileHeightPixels = 300;
                dimensionSource = 'default';
            }
        }
    }

    // Ensure we have valid dimensions
    if (!tileWidthPixels || !tileHeightPixels || tileWidthPixels <= 0 || tileHeightPixels <= 0) {
        console.warn("Invalid tile dimensions detected, using defaults");
        tileWidthPixels = 300;
        tileHeightPixels = 300;
        dimensionSource = 'fallback-default';
    }

    console.log(`Final canvas calculation using ${dimensionSource}: ${gridCols}x${gridRows} grid, tiles: ${tileWidthPixels}x${tileHeightPixels}px`);

    // Store discovered dimensions in localStorage for future use
    try {
        const dimensionsToStore = {
            tileWidthPixels,
            tileHeightPixels,
            rows: gridRows,
            columns: gridCols,
            source: dimensionSource,
            timestamp: new Date().toISOString()
        };
        localStorage.setItem('quiltTileDimensions', JSON.stringify(dimensionsToStore));
    } catch (e) {
        console.log('Could not save dimensions to localStorage', e);
    }

    // MODIFIED CALCULATION APPROACH:
    // 1. First calculate the CONTENT size based on actual tile dimensions
    const baseContentWidth = gridCols * tileWidthPixels;
    const baseContentHeight = gridRows * tileHeightPixels;
    
    // Scale content to fit within reasonable bounds while maintaining aspect ratio
    const maxContentSize = 800;
    const contentAspectRatio = baseContentWidth / baseContentHeight;
    
    let contentWidth, contentHeight;
    
    if (baseContentWidth > maxContentSize || baseContentHeight > maxContentSize) {
        // Scale down if too large
        if (contentAspectRatio >= 1) {
            // Wider than tall
            contentWidth = maxContentSize;
            contentHeight = maxContentSize / contentAspectRatio;
        } else {
            // Taller than wide
            contentHeight = maxContentSize;
            contentWidth = maxContentSize * contentAspectRatio;
        }
    } else {
        // Use actual dimensions if they fit
        contentWidth = baseContentWidth;
        contentHeight = baseContentHeight;
    }

    // 2. Now add binding and border to determine final canvas size
    // BINDING AND BORDER SIZES - DECREASED BY ANOTHER 20%:
    // Binding: 2.35008% of content width per side (was 2.9376%, decreased by 20%)
    // Border: 3.9168% of content width per side (was 4.896%, decreased by 20%)
    const bindingSize = contentWidth * (showBinding ? 0.0235008 : 0); // DECREASED from 0.029376 to 0.0235008
    const borderSize = contentWidth * (showBorder ? 0.039168 : 0);    // DECREASED from 0.04896 to 0.039168
    
    // Calculate final canvas dimensions
    const finalWidth = contentWidth + (2 * bindingSize) + (2 * borderSize);
    const finalHeight = contentHeight + (2 * bindingSize) + (2 * borderSize);
    
    // Update canvas dimensions
    setCanvasDimensions({ 
        width: finalWidth, 
        height: finalHeight,
        contentWidth: contentWidth,
        contentHeight: contentHeight,
        contentAspectRatio: contentAspectRatio,
        actualTileWidth: tileWidthPixels,
        actualTileHeight: tileHeightPixels
    });
    
    // Save display info for debugging
    setDisplayInfo({
        gridCols,
        gridRows,
        tileWidth: tileWidthPixels,
        tileHeight: tileHeightPixels,
        aspectRatio: tileWidthPixels / tileHeightPixels,
        dimensionSource,
        baseContentWidth,
        baseContentHeight,
        scaledContentWidth: contentWidth,
        scaledContentHeight: contentHeight
    });
    
  }, [firestoreDimensions, selectedLayout, showBinding, showBorder]);

  // Improved function to analyze image dimensions with more robust handling
  const analyzeImageDimensions = (img, tile) => {
    if (!img || tile.dimensionsAnalyzed) return;
    
    // Mark this tile as analyzed to prevent repeated analysis
    tile.dimensionsAnalyzed = true;
    
    // Get actual image dimensions
    const imgWidth = img.naturalWidth;
    const imgHeight = img.naturalHeight;
    
    if (imgWidth && imgHeight && imgWidth > 50 && imgHeight > 50) {
      // Add to consolidated dimensions
      consolidateImageAnalysis(imgWidth, imgHeight);
      
      // Log dimensions at reasonable intervals
      if (!analyzedDimensions || analyzedDimensions.count % 5 === 0) {
        console.log(`Analyzed image dimensions: ${imgWidth}x${imgHeight}`);
      }
    }
  };

  // Render the quilt to canvas
  useEffect(() => {
        setIsRendering(true);
        setRenderError(null);

        const renderQuilt = async () => {
            try {
                const canvas = effectiveCanvasRef.current;
                if (!canvas) {
                    console.error("Canvas ref not available");
                    return;
                }
                
                // Detect Safari browser for specific handling
                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent) || 
                                /iPad|iPhone|iPod/.test(navigator.userAgent);
                
                // Set canvas dimensions with device pixel ratio for crisp text
                const devicePixelRatio = window.devicePixelRatio || 1;
                const displayWidth = canvasDimensions.width;
                const displayHeight = canvasDimensions.height;
                
                // Scale canvas for high DPI displays
                canvas.width = displayWidth * devicePixelRatio;
                canvas.height = displayHeight * devicePixelRatio;
                canvas.style.width = displayWidth + 'px';
                canvas.style.height = displayHeight + 'px';
                
                // Get canvas context with Safari-specific settings
                const ctx = canvas.getContext('2d', { 
                    alpha: false, 
                    antialias: false, 
                    desynchronized: false, 
                    willReadFrequently: false
                });
                if (!ctx) {
                    throw new Error("Could not get canvas context");
                }

                // Scale context to match device pixel ratio for crisp text
                ctx.scale(devicePixelRatio, devicePixelRatio);

                // Safari-specific rendering settings - FIXED approach
                if (isSafari) {
                    // For Safari, keep smoothing OFF for tiles but ON for text
                    ctx.imageSmoothingEnabled = false;
                    ctx.webkitImageSmoothingEnabled = false;
                } else {
                    // For other browsers, disable smoothing completely
                    ctx.imageSmoothingEnabled = false;
                    if (ctx.webkitImageSmoothingEnabled !== undefined) {
                        ctx.webkitImageSmoothingEnabled = false;
                    }
                    if (ctx.mozImageSmoothingEnabled !== undefined) {
                        ctx.mozImageSmoothingEnabled = false;
                    }
                    if (ctx.msImageSmoothingEnabled !== undefined) {
                        ctx.msImageSmoothingEnabled = false;
                    }
                }
                
                // Enhanced canvas clearing for Safari
                ctx.clearRect(0, 0, displayWidth, displayHeight);
                
                // Safari-specific: Force complete canvas reset
                if (isSafari) {
                    ctx.fillStyle = 'white';
                    ctx.fillRect(0, 0, displayWidth, displayHeight);
                }

        // Log current colors for debugging
        console.log('Rendering with colors:', { bindingColor, borderColor, showBinding, showBorder });

        // MODIFIED RENDERING APPROACH - WORK FROM INSIDE OUT:
        // 1. First determine exact content dimensions (should be same as what we calculated above)
        const contentWidth = canvasDimensions.contentWidth || 800;
        const contentHeight = canvasDimensions.contentHeight || 800;
        
        // 2. Calculate sizes for binding and border with FIXED PIXEL SIZES for precision
        // DECREASED PERCENTAGES BY ANOTHER 20%:
        const bindingSize = showBinding ? Math.round(contentWidth * 0.0235008) : 0; // DECREASED from 0.029376 to 0.0235008
        const borderSize = showBorder ? Math.round(contentWidth * 0.039168) : 0;    // DECREASED from 0.04896 to 0.039168
        
        // Ensure minimum sizes for visibility with decreased minimums (also 20% less)
        const actualBindingSize = showBinding ? Math.max(bindingSize, 13) : 0; // DECREASED from 16 to 13px minimum
        const actualBorderSize = showBorder ? Math.max(borderSize, 18) : 0;    // DECREASED from 23 to 18px minimum

        // 3. Calculate starting position of content area (centered)
        const contentX = actualBindingSize + actualBorderSize;
        const contentY = actualBindingSize + actualBorderSize;
        
        // 4. COMPLETE REDRAW - FIXED approach with CORRECT layer order
        // Start with a clean white canvas
        ctx.fillStyle = '#FFFFFF';
        ctx.fillRect(0, 0, displayWidth, displayHeight);

        // LAYER 1: Binding (outermost layer) - if enabled
        if (showBinding) {
            ctx.fillStyle = bindingColor;
            ctx.fillRect(0, 0, displayWidth, displayHeight);
            console.log(`Drew binding with color ${bindingColor}`);
        }
        
        // LAYER 2: Border (middle layer) - if enabled, sits inside the binding
        if (showBorder) {
            ctx.fillStyle = borderColor;
            // Create border rectangle that sits inside the binding
            const borderX = actualBindingSize;
            const borderY = actualBindingSize;
            const borderWidth = displayWidth - (2 * actualBindingSize);
            const borderHeight = displayHeight - (2 * actualBindingSize);
            
            ctx.fillRect(borderX, borderY, borderWidth, borderHeight);
            console.log(`Drew border with color ${borderColor} at ${borderX},${borderY} size ${borderWidth}x${borderHeight}`);
        }
        
        // LAYER 3: Content area (innermost) - white background for tiles
        ctx.fillStyle = '#FFFFFF';
        const contentAreaX = contentX;
        const contentAreaY = contentY;
        const contentAreaWidth = displayWidth - (2 * actualBindingSize) - (2 * actualBorderSize);
        const contentAreaHeight = displayHeight - (2 * actualBindingSize) - (2 * actualBorderSize);
        
        ctx.fillRect(contentAreaX, contentAreaY, contentAreaWidth, contentAreaHeight);
        console.log(`Drew content area at ${contentAreaX},${contentAreaY} size ${contentAreaWidth}x${contentAreaHeight}`);

        // Determine grid dimensions
        let gridRows, gridCols;
        let tilesToRender = [];
        
        // Get grid dimensions from Firestore or layout
        if (firestoreDimensions) {
          gridRows = firestoreDimensions.rows || 6;
          gridCols = firestoreDimensions.columns || 6;
        } else if (selectedLayout) {
          if (selectedLayout.gridSize) {
            const dimensions = selectedLayout.gridSize.split('x').map(Number);
            gridCols = dimensions[0] || 6;
            gridRows = dimensions[1] || 6;
          } else if (selectedLayout.original_grid_size) {
            const dimensions = selectedLayout.original_grid_size.split('x').map(Number);
            gridCols = dimensions[0] || 6;
            gridRows = dimensions[1] || 6;
          } else {
            gridCols = 6;
            gridRows = 6;
          }
        } else {
          gridRows = 6;
          gridCols = 6;
        }
        
        // Calculate exact tile dimensions for rendering
        // Use content area dimensions which remain constant regardless of border/binding
        const exactTileWidth = contentAreaWidth / gridCols;
        const exactTileHeight = contentAreaHeight / gridRows;
        
        // Create a grid-to-pixel mapping for perfect alignment
        // IMPROVED: This approach eliminates all gaps between tiles with better precision
        const gridToPixel = {
          x: [],  // x-coordinate of each column
          y: [],  // y-coordinate of each row
          width: [],  // width of each column
          height: []  // height of each row
        };
        
        // SAFARI-SPECIFIC: Force all positioning to integers and use larger overlaps
        const safariOverlap = isSafari ? 2 : 1; // Overlap for Safari
        
        // IMPROVED: Calculate column positions with precise pixel boundaries
        for (let col = 0; col <= gridCols; col++) {
            if (col === 0) {
                gridToPixel.x[col] = Math.round(contentAreaX);
            } else {
                gridToPixel.x[col] = Math.round(contentAreaX + (col * exactTileWidth));
            }
            
            if (col < gridCols) {
                // Calculate width as the difference between next x and current x
                const nextX = col === gridCols - 1 ? 
                    Math.round(contentAreaX + contentAreaWidth) : 
                    Math.round(contentAreaX + ((col + 1) * exactTileWidth));
                gridToPixel.width[col] = nextX - gridToPixel.x[col] + safariOverlap;
            }
        }
        
        // IMPROVED: Calculate row positions with precise pixel boundaries
        for (let row = 0; row <= gridRows; row++) {
            if (row === 0) {
                gridToPixel.y[row] = Math.round(contentAreaY);
            } else {
                gridToPixel.y[row] = Math.round(contentAreaY + (row * exactTileHeight));
            }
            
            if (row < gridRows) {
                // Calculate height as the difference between next y and current y
                const nextY = row === gridRows - 1 ? 
                    Math.round(contentAreaY + contentAreaHeight) : 
                    Math.round(contentAreaY + ((row + 1) * exactTileHeight));
                gridToPixel.height[row] = nextY - gridToPixel.y[row] + safariOverlap;
            }
        }
        
        // Get tiles to render based on layout data
        if (selectedLayout?.tileData && Array.isArray(selectedLayout.tileData)) {
          tilesToRender = selectedLayout.tileData;
        } else if (selectedLayout?.imageUrl || selectedLayout?.quilt_image_url || selectedLayout?.image_url) {
          // Single image case
          const imageUrl = selectedLayout.imageUrl || 
                          selectedLayout.quilt_image_url || 
                          selectedLayout.image_url;
          
          tilesToRender = [{
            url: imageUrl,
            isFullImage: true,
            row: 0,
            col: 0,
            rowSpan: gridRows,
            colSpan: gridCols
          }];
        }
        
        // Load and draw all tiles
        const tilePromises = tilesToRender.map(tile => {
          return new Promise((resolve) => {
            if (!tile.url) {
              resolve();
              return;
            }
            
            const img = new Image();
            img.crossOrigin = "anonymous";
            
            img.onload = () => {
              // Analyze image dimensions to improve future renders
              analyzeImageDimensions(img, tile);
              
              if (tile.isFullImage) {
                // Full image - draw across the entire content area
                ctx.drawImage(img, contentX, contentY, contentWidth, contentHeight);
                resolve();
                return;
              }
              
              // Regular tile
              const row = tile.row || 0;
              const col = tile.col || 0;
              const rowSpan = tile.rowSpan || 1;
              const colSpan = tile.colSpan || 1;
              
              // Validate position
              if (row >= gridRows || col >= gridCols) {
                console.warn(`Tile position out of bounds: row=${row}, col=${col}`);
                resolve();
                return;
              }
              
              // Calculate tile position and size with Safari-specific aggressive overlap
              const x = Math.floor(gridToPixel.x[col]);
              const y = Math.floor(gridToPixel.y[row]);
              
              const endCol = Math.min(col + colSpan, gridCols);
              const endRow = Math.min(row + rowSpan, gridRows);
              const width = Math.floor(gridToPixel.x[endCol] - x + safariOverlap);
              const height = Math.floor(gridToPixel.y[endRow] - y + safariOverlap);
              
              // Safari-specific background fill - much more aggressive extension
              ctx.fillStyle = '#FFFFFF';
              const bgExtension = isSafari ? 2 : 1;
              ctx.fillRect(x - bgExtension, y - bgExtension, width + (bgExtension * 2), height + (bgExtension * 2));
              
              // Calculate image fitting
              const imgAspect = img.width / img.height;
              const cellAspect = width / height;
              
              let drawX = x, drawY = y;
              let drawWidth = width, drawHeight = height;
              
              if (Math.abs(imgAspect - cellAspect) > 0.01) {
                if (imgAspect > cellAspect) {
                  drawWidth = width;
                  drawHeight = drawWidth / imgAspect;
                  drawY = y + ((height - drawHeight) / 2);
                } else {
                  drawHeight = height;
                  drawWidth = drawHeight * imgAspect;
                  drawX = x + ((width - drawWidth) / 2);
                }
              }
              
              // Safari-specific image drawing with completely different approach
              if (isSafari) {
                // Method 1: Draw the main image with significant overlap
                ctx.drawImage(img, 
                  drawX - 5, 
                  drawY - 5, 
                  drawWidth + 10, 
                  drawHeight + 10
                );
                
                // Additional fill passes for stubborn gaps
                ctx.fillStyle = ctx.createPattern(img, 'repeat');
                ctx.fillRect(x - 3, y - 3, width + 6, height + 6);
                
                // Final exact positioning pass
                ctx.drawImage(img, drawX, drawY, drawWidth, drawHeight);
              } else {
                // Standard drawing for non-Safari browsers
                const imgExtension = 1; // Define imgExtension for non-Safari browsers
                ctx.drawImage(img, drawX - imgExtension, drawY - imgExtension, 
                  drawWidth + (imgExtension * 2), drawHeight + (imgExtension * 2));
              }
              
              resolve();
            };
            
            img.onerror = () => {
              // Draw placeholder for failed image
              const row = tile.row || 0;
              const col = tile.col || 0;
              
              const x = gridToPixel.x[col];
              const y = gridToPixel.y[row];
              const width = gridToPixel.width[col];
              const height = gridToPixel.height[row];
              
              // Gray background
              ctx.fillStyle = '#F8F8F8';
              ctx.fillRect(x, y, width, height);
              
              // Border
              ctx.strokeStyle = '#DDDDDD';
              ctx.lineWidth = 1;
              ctx.strokeRect(x, y, width, height);
              
              resolve();
            };
            
            img.src = tile.url;
          });
        });
        
        // Wait for all tiles to be rendered
        await Promise.all(tilePromises);
        
        // Render embossed items with Safari fixes
        if (embossedItems && embossedItems.length > 0) {
          const embossPromises = embossedItems.map(item => {
            return new Promise((resolve) => {
              if (!item.dataUrl) {
                resolve();
                return;
              }
              
              const img = new Image();
              img.crossOrigin = "anonymous";
              
              img.onload = () => {
                // Always use fixed scale of 2.0 regardless of grid size
                const BASE_SCALE = 0.12; // Base scale factor
                const userScale = item.scale || 2.0; // Default to 2.0
                const baseSize = item.fixedSize || 100;
                const scaleFactor = BASE_SCALE * (baseSize / 100) * userScale;
                
                // Calculate target size based on smaller content dimension
                const smallerDimension = Math.min(contentWidth, contentHeight);
                const targetSize = smallerDimension * scaleFactor;
                
                // Position based on percentage of content area
                const x = contentX + (contentWidth * item.x / 100);
                const y = contentY + (contentHeight * item.y / 100);
                
                // Calculate dimensions preserving aspect ratio
                const imgAspect = img.width / img.height;
                let finalWidth, finalHeight;
                
                if (imgAspect > 1) {
                  // Wider image
                  finalWidth = targetSize;
                  finalHeight = targetSize / imgAspect;
                } else {
                  // Taller image
                  finalHeight = targetSize;
                  finalWidth = targetSize * imgAspect;
                }
                
                // Apply transformations and draw image
                ctx.save();
                
                // Safari-specific: Enable smoothing for embossed items
                if (isSafari) {
                    ctx.imageSmoothingEnabled = true;
                    ctx.webkitImageSmoothingEnabled = true;
                    ctx.imageSmoothingQuality = 'high';
                }
                
                ctx.translate(x, y);
                if (item.rotation) {
                  ctx.rotate(item.rotation * Math.PI / 180);
                }
                
                // Draw image centered on position
                ctx.drawImage(
                  img, 
                  -finalWidth / 2, 
                  -finalHeight / 2, 
                  finalWidth, 
                  finalHeight
                );
                
                ctx.restore();
                resolve();
              };
              
              img.onerror = () => {
                console.error("Failed to load embossed image:", item.dataUrl);
                resolve();
              };
              
              img.src = item.dataUrl;
            });
          });
          
          await Promise.all(embossPromises);
        }
        
        // Render text items with MUCH BETTER text clarity
        if (textItems && textItems.length > 0) {
          textItems.forEach(item => {
            // Calculate position
            const x = contentX + (contentWidth * item.x / 100);
            const y = contentY + (contentHeight * item.y / 100);
            
            // Font style
            let fontStyle = '';
            if (item.bold) fontStyle += 'bold ';
            if (item.italic) fontStyle += 'italic ';
            
            // CRITICAL FIX: Don't scale font size with devicePixelRatio for preview
            // This prevents text from appearing too small/pixelated at normal zoom
            const fontSizeRatio = Math.min(contentWidth, contentHeight) / 800;
            const fontSize = Math.max(14, item.fontSize * fontSizeRatio); // Increased minimum from 12 to 14
            
            ctx.save();
            
            // CRITICAL FIX: Use different text rendering for preview vs download
            // For preview, prioritize readability over pixel-perfect rendering
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            // Enhanced browser-specific text rendering with focus on clarity
            if (isSafari) {
                ctx.webkitImageSmoothingEnabled = true;
                ctx.textRenderingOptimization = 'optimizeQuality';
                ctx.webkitFontSmoothing = 'antialiased';
                ctx.mozOsxFontSmoothing = 'grayscale';
            } else {
                // For Chrome/Firefox, use optimized text rendering
                if (ctx.webkitImageSmoothingEnabled !== undefined) {
                    ctx.webkitImageSmoothingEnabled = true;
                }
                if (ctx.mozImageSmoothingEnabled !== undefined) {
                    ctx.mozImageSmoothingEnabled = true;
                }
                if (ctx.msImageSmoothingEnabled !== undefined) {
                    ctx.msImageSmoothingEnabled = true;
                }
                if (ctx.textRenderingOptimization !== undefined) {
                    ctx.textRenderingOptimization = 'optimizeQuality';
                }
                if (ctx.mozOsxFontSmoothing !== undefined) {
                    ctx.mozOsxFontSmoothing = 'grayscale';
                }
            }
            
            ctx.translate(x, y);
            
            if (item.rotation) {
              ctx.rotate(item.rotation * Math.PI / 180);
            }
            
            // CRITICAL: Use system fonts for better rendering
            const fontFamily = item.fontFamily || 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
            ctx.font = `${fontStyle}${fontSize}px ${fontFamily}`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            
            // Better text measurement
            const metrics = ctx.measureText(item.text);
            const textWidth = metrics.width;
            const textHeight = fontSize * 1.2;
            
            // Standard padding
            const paddingX = 12;
            const paddingY = 8;
            
            // Draw background if specified
            if (item.backgroundColor) {
              ctx.fillStyle = item.backgroundColor;
              ctx.fillRect(
                -textWidth/2 - paddingX, 
                -textHeight/2 - paddingY, 
                textWidth + (paddingX * 2), 
                textHeight + (paddingY * 2)
              );
            }
            
            // CRITICAL: Set text color and draw text
            ctx.fillStyle = item.color || '#000000';
            
            // IMPROVED: Use subpixel positioning for sharper text
            const textX = Math.round(0 * devicePixelRatio) / devicePixelRatio;
            const textY = Math.round(0 * devicePixelRatio) / devicePixelRatio;
            
            // Enhanced text drawing - no stroke for cleaner appearance
            ctx.fillText(item.text, textX, textY);
            
            // Draw border if specified
            if (item.showBorder && item.borderColor) {
              ctx.strokeStyle = item.borderColor;
              ctx.lineWidth = 2;
              ctx.strokeRect(
                -textWidth/2 - paddingX, 
                -textHeight/2 - paddingY, 
                textWidth + (paddingX * 2), 
                textHeight + (paddingY * 2)
              );
            }
            
            // Draw underline if specified
            if (item.underline) {
              ctx.beginPath();
              ctx.moveTo(-textWidth/2, fontSize/2 + 2);
              ctx.lineTo(textWidth/2, fontSize/2 + 2);
              ctx.strokeStyle = item.color || '#000000';
              ctx.lineWidth = Math.max(1, fontSize * 0.05);
              ctx.stroke();
            }
            
            ctx.restore();
          });
        }
        
        // Call completion handler if provided
        if (onRenderComplete) {
          onRenderComplete(canvas);
        }
        
        setIsRendering(false);
      } catch (error) {
        console.error("Error rendering quilt to canvas:", error);
        setRenderError(error.message);
        setIsRendering(false);
      }
    };
    
    // Use a small timeout to ensure proper DOM updates before redrawing
    // This helps prevent ghost images by giving the browser time to process updates
    const renderTimeout = setTimeout(() => {
      renderQuilt();
    }, 10);
    
    return () => clearTimeout(renderTimeout);
    
  }, [
    canvasDimensions,
    effectiveCanvasRef,
    selectedLayout,
    showBinding,
    showBorder, 
    bindingColor,
    borderColor,
    embossedItems,
    textItems,
    onRenderComplete,
    firestoreDimensions
  ]);

  return (
    <div className="canvas-quilt-renderer relative w-full h-full">
      {isRendering && (
        <div className="absolute inset-0 bg-gray-50 bg-opacity-70 flex items-center justify-center">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
            <p className="mt-2 text-sm text-gray-600">Rendering...</p>
          </div>
        </div>
      )}
      
      {renderError && (
        <div className="absolute inset-0 bg-red-50 bg-opacity-70 flex items-center justify-center">
          <div className="text-center text-red-600 p-4">
            <p>Error rendering quilt: {renderError}</p>
          </div>
        </div>
      )}
      
      <canvas 
        ref={effectiveCanvasRef}
        width={canvasDimensions.width}
        height={canvasDimensions.height}
        className="max-w-full max-h-full mx-auto"
        data-component="CanvasQuiltRenderer"
        data-layout-name={selectedLayout?.layout_name || "Unknown"}
        data-grid-size={firestoreDimensions ? 
          `${firestoreDimensions.columns}x${firestoreDimensions.rows}` : 
          (selectedLayout?.gridSize || selectedLayout?.original_grid_size || "unknown")}
        data-tile-dimensions={displayInfo ? 
          `${displayInfo.tileWidth}x${displayInfo.tileHeight}` : 
          (firestoreDimensions ? 
            `${firestoreDimensions.tileWidthPixels}x${firestoreDimensions.tileHeightPixels}` : 
            "unknown")}
      />
    </div>
  );
};

export default CanvasQuiltRenderer;